import React from 'react';
import axios from 'axios';

import { skipBackendProbeForApp } from 'lib/backendAppProbe';
import { FEATURES_NPS_PATH } from 'lib/config';
import { errorGenerator } from 'lib/errorGenerator';
import { generatoreCode } from 'lib/generateCode';
import { useDump } from 'lib/useDump';
import Paths from 'lib/paths';
import { useAppInitiator } from 'providers/AppInitiator';

import NpsQuestionnaireModal from './NpsQuestionnaireModal';
import {
  extractNpsEligibilityPayload,
  shouldShowNpsQuestionnaire,
} from './parseNpsEligibilityResponse';

const NPS_DELAY_MS =
  process.env.NODE_ENV === 'development' ? 2000 : 30000;
const NPS_LOG_PREFIX = '[NPS]';

function handleNpsEligibilityApiFailure(error: unknown) {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 401) {
      window.open('/rqx/session/logout', '_self');
      return;
    }
    const code = generatoreCode(8).toString();
    errorGenerator(
      code,
      error,
      'apiError npsEligibility',
      'GET /rqx/api/features/nps',
    );
    return;
  }
  const code = generatoreCode(8).toString();
  errorGenerator(
    code,
    error,
    'apiError npsEligibility',
    'GET /rqx/api/features/nps (network)',
  );
}

function isAbortError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  return error.code === 'ERR_CANCELED' || error.code === 'ECONNABORTED';
}

/**
 * Eligibility NPS dopo sessione pronta (InitProvider): GET al cambio hash app.
 * 404 silenzioso; 401/403/5xx come altre API autenticate.
 */
const NpsEligibility = () => {
  const { boot } = useAppInitiator();
  const { dump } = useDump();

  const [hash, setHash] = React.useState(() => window.location.hash);

  React.useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const appCode = React.useMemo(() => {
    const raw = hash.replace(/^#/, '').trim();
    const segment = raw.split('&')[0] ?? '';
    return segment.toLowerCase();
  }, [hash]);

  const appName = React.useMemo(() => {
    const app = dump?.apps?.find((a) => a.code === appCode);
    return app?.label || appCode;
  }, [dump?.apps, appCode]);

  const [activeQuestionnaireId, setActiveQuestionnaireId] = React.useState<
    string | null
  >(null);

  const handleCloseModal = React.useCallback(() => {
    setActiveQuestionnaireId(null);
  }, []);

  React.useEffect(() => {
    const ac = new AbortController();
    let timerId: number | undefined;

    console.log(`${NPS_LOG_PREFIX} effect start`, { appCode, hash });

    if (skipBackendProbeForApp(appCode)) {
      console.log(
        `${NPS_LOG_PREFIX} skip probe reason=excluded_app appCode="${appCode}"`,
      );
      return () => {
        ac.abort();
      };
    }

    const url = Paths.join(
      Paths.join(boot.backends.api.baseUrl, FEATURES_NPS_PATH),
      appCode,
    );
    console.log(`${NPS_LOG_PREFIX} GET eligibility`, { url, appCode });

    (async () => {
      try {
        const response = await axios.get(url, {
          signal: ac.signal,
          withCredentials: true,
          maxRedirects: 5,
          validateStatus: (s) => s === 200 || s === 404,
        });

        if (ac.signal.aborted) {
          console.log(`${NPS_LOG_PREFIX} aborted after response appCode=${appCode}`);
          return;
        }

        console.log(`${NPS_LOG_PREFIX} response`, {
          appCode,
          status: response.status,
        });

        if (response.status === 404) {
          console.log(
            `${NPS_LOG_PREFIX} modal hidden reason=api_404_nps_disabled appCode=${appCode}`,
          );
          return;
        }

        const payload = extractNpsEligibilityPayload(response.data);
        if (!shouldShowNpsQuestionnaire(payload)) {
          const backendReason =
            payload?.reason ?? 'backend_said_no_questionnaire';
          console.log(
            `${NPS_LOG_PREFIX} modal hidden reason=${backendReason} appCode=${appCode}`,
            { outcome: payload?.outcome },
          );
          return;
        }

        const qid = String(payload!.questionnaireToShow).trim();

        if (ac.signal.aborted) {
          console.log(`${NPS_LOG_PREFIX} aborted before timer appCode=${appCode}`);
          return;
        }

        console.log(
          `${NPS_LOG_PREFIX} scheduling modal in ${NPS_DELAY_MS}ms`,
          { appCode, questionnaireId: qid },
        );
        timerId = window.setTimeout(() => {
          if (!ac.signal.aborted) {
            console.log(`${NPS_LOG_PREFIX} opening modal`, {
              appCode,
              questionnaireId: qid,
            });
            setActiveQuestionnaireId(qid);
          }
        }, NPS_DELAY_MS);
      } catch (error) {
        if (ac.signal.aborted || isAbortError(error)) {
          console.log(`${NPS_LOG_PREFIX} request aborted/canceled appCode=${appCode}`);
          return;
        }
        console.warn(`${NPS_LOG_PREFIX} request failed appCode=${appCode}`, error);
        handleNpsEligibilityApiFailure(error);
      }
    })();

    return () => {
      console.log(`${NPS_LOG_PREFIX} effect cleanup`, {
        appCode,
        hadTimer: timerId !== undefined,
      });
      ac.abort();
      if (timerId !== undefined) {
        window.clearTimeout(timerId);
      }
      setActiveQuestionnaireId(null);
    };
  }, [appCode, boot.backends.api.baseUrl]);

  if (!activeQuestionnaireId) {
    return null;
  }

  return (
    <NpsQuestionnaireModal
      open
      appCode={appCode}
      appName={appName}
      apiBaseUrl={boot.backends.api.baseUrl}
      questionnaireId={activeQuestionnaireId}
      onClose={handleCloseModal}
    />
  );
};

export default NpsEligibility;
